/**
 * Zamanlanmış görevlerin saf modeli — durum/tip eşlemesi ve zaman hesapları.
 *
 * Bu değerler veritabanında sayı (WorkerTaskStateId, WorkerTaskTypeId); ekranda ise
 * anlamlı bir ad, renk ve simge gerekiyor. Eşleme tek yerde tutuluyor ki liste, detay
 * ve düzenleme formu aynı dili konuşsun.
 */

export const WORKER_STATE = { queued: 1, processing: 2, error: 3, success: 4 } as const;
export const WORKER_TYPE = { mail: 1, print: 2, sp: 3, whatsapp: 4 } as const;

export type WorkerStateKey = 'queued' | 'processing' | 'error' | 'success' | 'paused' | 'all';
export type WorkerTypeKey = 'mail' | 'print' | 'sp' | 'whatsapp';

export interface WorkerTask {
  workerTaskId: number;
  workerTaskTypeId: number;
  workerTaskStateId: number;
  taskName: string | null;
  executeAfter: string | null;
  isRepetitive: boolean;
  isPause: boolean;
  isComplete: boolean;
  intervalSeconds: number | null;
  executedAt: string | null;
  executionResult: string | null;
  fromAlias?: string | null;
  subject?: string | null;
  body?: string | null;
  recipients?: string | null;
  cc?: string | null;
  bcc?: string | null;
  templateName?: string | null;
  templateLanguage?: string | null;
  templateParameters?: string | null;
  addDate?: string | null;
  updateDate?: string | null;
}

export interface WorkerStateCounts {
  queued: number;
  processing: number;
  error: number;
  success: number;
  paused: number;
  all: number;
}

export function typeKeyOf(typeId: number): WorkerTypeKey {
  switch (typeId) {
    case WORKER_TYPE.print:
      return 'print';
    case WORKER_TYPE.sp:
      return 'sp';
    case WORKER_TYPE.whatsapp:
      return 'whatsapp';
    default:
      return 'mail';
  }
}

/**
 * Satırda gösterilecek durum.
 *
 * Duraklatılmış görev veritabanında hâlâ "kuyrukta" görünüyor (`WorkerTaskStateId = 1`)
 * ama kuyruktan hiç alınmıyor; listede "kuyrukta" demek kullanıcıya yanlış bilgi verirdi.
 */
export function stateKeyOf(
  task: Pick<WorkerTask, 'workerTaskStateId' | 'isPause'>,
): Exclude<WorkerStateKey, 'all'> {
  if (task.isPause) return 'paused';
  switch (task.workerTaskStateId) {
    case WORKER_STATE.processing:
      return 'processing';
    case WORKER_STATE.error:
      return 'error';
    case WORKER_STATE.success:
      return 'success';
    default:
      return 'queued';
  }
}

/** Tekrarlı görevin aralığı: saniye → "15 dk", "2 sa", "1 gün". */
export function formatInterval(seconds: number | null | undefined): string | null {
  if (!seconds || seconds <= 0) return null;
  if (seconds % 86400 === 0) return `${seconds / 86400} gün`;
  if (seconds % 3600 === 0) return `${seconds / 3600} sa`;
  if (seconds % 60 === 0) return `${seconds / 60} dk`;
  return `${seconds} sn`;
}

/**
 * "Ne zaman çalışacak" — mutlak tarih yerine göreli metin.
 *
 * Kuyruğu izleyen kişi "14.07.2026 07:00" tarihinden çok "3 dakika sonra" ya da
 * "12 dakikadır gecikmiş" bilgisini arıyor: gecikme, worker'ın çalışmadığının ilk
 * işareti ve masaüstü ekranında hiç görünmüyordu.
 */
export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const diffSec = Math.round((at.getTime() - now.getTime()) / 1000);
  const abs = Math.abs(diffSec);

  const unit =
    abs < 60
      ? `${abs} sn`
      : abs < 3600
        ? `${Math.round(abs / 60)} dk`
        : abs < 86400
          ? `${Math.round(abs / 3600)} sa`
          : `${Math.round(abs / 86400)} gün`;

  if (diffSec >= 0) return `${unit} sonra`;
  return `${unit} önce`;
}

/**
 * Bekleyen görev gecikmiş mi? (çalışma zamanı geçti ama hâlâ kuyrukta)
 *
 * Tolerans veriliyor: worker turu birkaç saniyede bir dönüyor, saniyelik sapmayı
 * "gecikme" diye göstermek yanlış alarm olurdu.
 */
export function isOverdue(
  task: Pick<WorkerTask, 'workerTaskStateId' | 'isPause' | 'executeAfter'>,
  now: Date = new Date(),
  toleranceSec = 120,
): boolean {
  if (task.isPause || task.workerTaskStateId !== WORKER_STATE.queued || !task.executeAfter) return false;
  const at = new Date(task.executeAfter);
  if (Number.isNaN(at.getTime())) return false;
  return (now.getTime() - at.getTime()) / 1000 > toleranceSec;
}

/** Hata metninin satırda gösterilecek kısa hali (ilk cümle / ilk satır). */
export function shortResult(result: string | null | undefined, max = 90): string | null {
  if (!result) return null;
  const firstLine =
    result
      .split(/[\r\n]/)
      .find((l) => l.trim().length > 0)
      ?.trim() ?? '';
  if (!firstLine) return null;
  return firstLine.length <= max ? firstLine : `${firstLine.slice(0, max)}…`;
}

/**
 * Worker, kredi bittiğinde görevi HATA yapmıyor; sonucu "[KREDI]" önekiyle işaretleyip
 * kuyrukta bırakıyor (kredi gelince aynı görev gönderilecek). Bu satırları normal hata
 * gibi göstermek yanlış: yapılacak iş kod düzeltmek değil kredi yüklemek.
 */
export const CREDIT_WAITING_PREFIX = '[KREDI]';

export function isCreditWaiting(task: Pick<WorkerTask, 'executionResult'>): boolean {
  return !!task.executionResult && task.executionResult.startsWith(CREDIT_WAITING_PREFIX);
}

export interface WhatsAppCredit {
  configured: boolean;
  hasCredit: boolean;
  unlimited?: boolean;
  remaining?: number | null;
  totalLimit?: number | null;
}

/** Başlıkta görünen kredi metni: sınırsız kurulumda sayı yazmak yanıltıcı olurdu. */
export function formatCredit(credit: WhatsAppCredit | null | undefined): string {
  if (!credit) return '…';
  if (credit.unlimited) return 'Sınırsız';
  return new Intl.NumberFormat('tr-TR').format(credit.remaining ?? 0);
}

/** Saniye ↔ {değer, birim} — form aralık kutusu için. */
export function splitInterval(seconds: number | null | undefined): {
  value: number;
  unit: 'minutes' | 'hours' | 'days';
} {
  const s = seconds && seconds > 0 ? seconds : 3600;
  if (s % 86400 === 0) return { value: s / 86400, unit: 'days' };
  if (s % 3600 === 0) return { value: s / 3600, unit: 'hours' };
  return { value: Math.max(1, Math.round(s / 60)), unit: 'minutes' };
}

export function joinInterval(value: number, unit: 'minutes' | 'hours' | 'days'): number {
  const v = Math.max(1, Math.round(value || 1));
  return unit === 'days' ? v * 86400 : unit === 'hours' ? v * 3600 : v * 60;
}
